// src/pages/ContactPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './ContactPage.css';
import pb from '../lib/pocketbase';

function ContactPage() {
  const navigate = useNavigate();
  // Crée des états pour chaque champ du formulaire
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false); // Pour gérer l'état d'envoi
  const [submitMessage, setSubmitMessage] = useState(''); // Pour les messages de feedback

  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  // DÉCLARATION DE phoneNumber et fullAddress
  const phoneNumber = settings?.phone || "+33 7 69 29 79 63";
  const fullAddress = settings?.address || "1 rue du Tinal, 78000 Versailles, France";
  const contactEmail = settings?.email || 'contact@infocolab.com';
  const openingHours = settings?.hours || 'Lundi - Vendredi : 9h00 - 18h00';

  // Informations pour Helmet
  const siteUrl = "https://infocolab.com"; // Votre nom de domaine
  const pageUrl = `${siteUrl}/contact`;
  const pageTitle = "Contactez INFO COLAB - Discutons de Votre Projet Web à Versailles";
  const pageDescription = "Prenez contact avec l'agence web INFO COLAB à Versailles (78000) pour toute question, demande de devis ou pour démarrer votre projet de création de site internet, application web ou design.";
  const ogImageUrl = `${siteUrl}/images/og-contact.jpg`; // Pensez à créer cette image

  // URL d'intégration Google Maps (celle que vous avez fournie)
  const mapEmbedUrl = settings?.google_maps_url || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1313.843118103916!2d2.125090340068964!3d48.80696672230649!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e67dbbad4be331%3A0x669fcf827ab3b09d!2s19%20Rue%20du%20Peintre%20Lebrun%2C%2078000%20Versailles!5e0!3m2!1sfr!2sfr!4v1767191296120!5m2!1sfr!2sfr";

  useEffect(() => {
    let mounted = true;
    const fetchSettings = async () => {
      setLoadingSettings(true);
      try {
        const res = await pb.collection('site_contact_settings').getList(1, 1, { sort: '-created' });
        const it = res?.items?.[0] || null;
        if (mounted) setSettings(it);
      } catch {
        if (mounted) setSettings(null);
      } finally {
        if (mounted) setLoadingSettings(false);
      }
    };
    fetchSettings();
    return () => { mounted = false; };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(''); // Réinitialise le message précédent

    try {
      await pb.collection('contact_messages').create({
        full_name: name,
        email,
        subject: subject || 'Contact depuis le site',
        message,
        phone: '',
        status: 'new',
      });
      setSubmitMessage('Merci ! Votre message a bien été envoyé.');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (e) {
      // Fallback mailto si la règle PB ou le réseau bloque
      const to = contactEmail;
      const mailSubject = subject ? `${subject}` : 'Contact depuis le site';
      const bodyLines = [
        `Nom: ${name}`,
        `Email: ${email}`,
        '',
        'Message:',
        message,
      ];
      const mailBody = encodeURIComponent(bodyLines.join('\n'));
      const mailtoLink = `mailto:${to}?subject=${encodeURIComponent(mailSubject)}&body=${mailBody}`;
      window.location.href = mailtoLink;
      setSubmitMessage(e?.message ? `Impossible d'envoyer automatiquement (PocketBase). Ouverture de votre client email…` : `Ouverture de votre client email…`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="INFO COLAB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <div className="container"> {/* Conteneur global */}
        <section className="contact-page-section">
          <h1>Contactez-nous</h1>
          <p className="intro">
            Une question, un projet ? N'hésitez pas à nous contacter via le formulaire ci-dessous
            ou en utilisant nos coordonnées. Nous vous répondrons dans les plus brefs délais.
          </p>

          <div className="contact-content-wrapper">
            <div className="contact-form-container">
              <h2>Envoyez-nous un message</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Nom complet</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Adresse Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Sujet</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Votre message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    required
                  ></textarea>
                </div>
                <button type="submit" className="submit-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                </button>
                {submitMessage && <p className="submit-feedback">{submitMessage}</p>}
              </form>
            </div>

            <div className="contact-info-container">
              <h2>Nos Coordonnées</h2>
              <ul className="contact-info-list">
                <li>
                  <span role="img" aria-label="Adresse" style={{ marginRight: '10px' }}>📍</span>
                  {fullAddress}
                </li>
                <li>
                  <span role="img" aria-label="Téléphone" style={{ marginRight: '10px' }}>📞</span>
                  <a href={`tel:${phoneNumber.replace(/\s/g, '')}`}>{phoneNumber}</a>
                </li>
                <li>
                  <span role="img" aria-label="Email" style={{ marginRight: '10px' }}>📧</span>
                  <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
                </li>
                <li>
                  <span role="img" aria-label="Horaires" style={{ marginRight: '10px' }}>⏰</span>
                  {openingHours}
                </li>
              </ul>
              <div className="contact-map">
                {/* Intégration de la carte Google Maps */}
                {loadingSettings ? (
                  <div style={{ padding: 10, opacity: 0.8 }}>Chargement…</div>
                ) : (
                  <iframe
                    src={mapEmbedUrl}
                    width="100%" // Prendra la largeur du conteneur .contact-map
                    height="300" // Hauteur ajustable
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Notre localisation"
                  ></iframe>
                )}
              </div>
            </div>
          </div>
        </section>

        <div style={{ marginTop: 60, marginBottom: 60 }}>
          <div className="card" style={{ maxWidth: 800, margin: '0 auto', border: '1px solid #3b82f6', borderRadius: 10, padding: 30, background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white', textAlign: 'center' }}>
            <h3 style={{ marginTop: 0, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, fontSize: '1.5rem' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
              Rejoindre l'équipe
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem', margin: '16px auto', maxWidth: 600 }}>
              Vous souhaitez aller plus loin et devenir partenaire ? Postulez en quelques clics via votre compte existant.
            </p>
            <div style={{ display: 'flex', gap: 16, marginTop: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn" style={{ border: 'none', background: 'white', color: '#1e40af', fontWeight: 600, padding: '12px 24px', fontSize: '1rem', cursor: 'pointer', borderRadius: '8px' }} onClick={() => navigate('/candidature', { state: { role: 'commercial', skipIntro: true } })}>
                Devenir Commercial
              </button>
              <button className="btn" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', padding: '12px 24px', fontSize: '1rem', cursor: 'pointer', borderRadius: '8px' }} onClick={() => navigate('/candidature', { state: { role: 'developpeur', skipIntro: true } })}>
                Devenir Développeur
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ContactPage;